import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import db from './timesheetDB';
import { setOfflineDraftSaved, setOfflineQueueCount, setSyncing, setSyncComplete } from '../actions/timesheetOffline';
import { tneAxiosClient } from '../api/apiSettings';
import { timesheetsStatuses } from './constants';

export const useTimesheetOffline = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isOnline = useSelector((state) => state.pwa.isOnline);
  const syncedRef = useRef(false);

  // Save draft to IndexedDB
  const saveDraftOffline = useCallback(
    async (timesheetData) => {
      if (!timesheetData?.Id) return;

      try {
        await db.drafts.put({
          timesheetId: timesheetData.Id,
          userId: timesheetData.UserId,
          data: timesheetData,
          updatedAt: new Date().toISOString(),
          status: 'draft'
        });

        dispatch(setOfflineDraftSaved(timesheetData.Id, new Date().toISOString()));
        console.log('Draft saved offline:', timesheetData.Id);
      } catch (error) {
        console.error('Failed to save draft offline:', error);
      }
    },
    [dispatch]
  );

  // Queue submission for when back online
  const queueSubmission = useCallback(
    async (timesheetData, approver, isSplitFlow) => {
      if (!timesheetData?.Id) return;

      try {
        await db.submissionQueue.add({
          timesheetId: timesheetData.Id,
          userId: timesheetData.UserId,
          type: isSplitFlow ? 'split' : 'standard',
          approver,
          data: timesheetData,
          createdAt: new Date().toISOString(),
          status: 'queued'
        });

        const count = await db.submissionQueue.count();
        dispatch(setOfflineQueueCount(count));

        console.log('Submission queued for sync:', timesheetData.Id);
      } catch (error) {
        console.error('Failed to queue submission:', error);
      }
    },
    [dispatch]
  );

  // Load draft from IndexedDB
  const loadDraft = useCallback(async (timesheetId) => {
    if (!timesheetId) return null;

    try {
      const draft = await db.drafts.get(timesheetId);
      return draft || null;
    } catch (error) {
      console.error('Failed to load draft:', error);
      return null;
    }
  }, []);

  // Delete draft after successful submit/save
  const clearDraft = useCallback(async (timesheetId) => {
    if (!timesheetId) return;

    try {
      await db.drafts.delete(timesheetId);
      console.log('Draft cleared:', timesheetId);
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  }, []);

  // Load queue count on mount
  useEffect(() => {
    const loadQueueCount = async () => {
      try {
        const count = await db.submissionQueue.count();
        dispatch(setOfflineQueueCount(count));
      } catch (error) {
        console.error('Failed to load queue count:', error);
      }
    };

    loadQueueCount();
  }, [dispatch]);

  // Sync queued submissions when back online
  useEffect(() => {
    if (!isOnline || syncedRef.current) return;

    const syncQueue = async () => {
      const queuedItems = await db.submissionQueue.where('status').equals('queued').toArray();

      if (queuedItems.length === 0) return;

      dispatch(setSyncing(true));
      let syncedCount = 0;

      const syncItem = async (item) => {
        try {
          if (item.type === 'split') {
            const formData = new FormData();
            formData.append(
              'Payload',
              JSON.stringify({
                UserId: item.data.UserId,
                TimesheetId: item.data.Id,
                TimesheetSections: item.data.TimesheetSections.filter((section) => {
                  const status = section.Id ? section?.Status?.Status : timesheetsStatuses(t).draft.apiStatus;
                  return (
                    status !== timesheetsStatuses(t).approved.apiStatus &&
                    status !== timesheetsStatuses(t).processed.apiStatus
                  );
                }).map((section) => ({
                  ApproverId: section.ApproverId,
                  Approver: section.Approver,
                  ...(typeof section.Id !== 'string' ? { Id: section.Id } : {}),
                  SectionStatus: timesheetsStatuses(t).submitted.apiStatus,
                  TimesheetDetails: section.TimesheetDetails.map((detail) => ({
                    ...detail,
                    WorkTypeId: detail.WorkType.Id
                  })),
                  TimesheetPerDiem: section.TimesheetPerDiem,
                  Attachments: []
                }))
              })
            );

            await tneAxiosClient.post('v1/timesheet/sections/upsert', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } else {
            await tneAxiosClient.put(`v1/timesheet?Id=${item.data.Id}`, {
              ...item.data,
              TimesheetStatus: timesheetsStatuses(t).submitted.apiStatus,
              ApproverId: item.approver,
              TimesheetDetails: item.data.TimesheetDetails.map((detail) => ({
                ...detail,
                WorkTypeId: detail.WorkType.Id
              }))
            });
          }

          // Mark as synced
          await db.submissionQueue.update(item.id, { status: 'synced' });
          await clearDraft(item.timesheetId);
          syncedCount++;
        } catch (error) {
          console.error('Failed to sync item:', item.timesheetId, error);
          await db.submissionQueue.update(item.id, { status: 'failed' });
        }
      };

      // eslint-disable-next-line no-restricted-syntax -- items must sync sequentially, in queue order
      for (const item of queuedItems) {
        // eslint-disable-next-line no-await-in-loop -- sequential sync is intentional (ordering / server load)
        await syncItem(item);
      }

      // Clean up synced items
      await db.submissionQueue.where('status').equals('synced').delete();

      const remainingCount = await db.submissionQueue.count();
      dispatch(setOfflineQueueCount(remainingCount));
      dispatch(setSyncComplete(syncedCount));

      syncedRef.current = true;
      setTimeout(() => {
        syncedRef.current = false;
      }, 5000);
    };

    syncQueue();
  }, [isOnline, dispatch, clearDraft, t]);

  return {
    saveDraftOffline,
    queueSubmission,
    loadDraft,
    clearDraft
  };
};
